






import debounce from 'debounce';
import { Editor as TipTapEditor, EditorContent } from '@tiptap/vue-2';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Superscript from '@tiptap/extension-superscript';
import History from '@tiptap/extension-history';
import Focus from '@tiptap/extension-focus';
import Text from '@tiptap/extension-text';
import type { IField, IMergeValues, ITextContentDoc } from '@getaccept/editor-lib';
import {
  ProsemirrorHelper,
  EnterBreakNode,
  InputDocNode,
  MergeValueNode,
  ParagraphNode,
} from '@getaccept/editor-lib';
import { updateMergeValues, setContent, handleInit } from './helpers/prosemirror-viewer.helper';

@Component({
  components: {
    EditorContent,
  },
})
export default class ProsemirrorViewerFormInput extends Vue {
  @Prop() content: ITextContentDoc;
  @Prop({ default: true }) multiLine: boolean;
  @Prop({ default: true }) bold: boolean;
  @Prop({ default: true }) italic: boolean;
  @Prop({ default: true }) underline: boolean;
  @Prop({ default: true }) superscript: boolean;
  @Prop({ default: true }) label: boolean;
  @Prop({ default: true }) link: boolean;
  @Prop({ default: false }) fieldFocused?: boolean;
  @Prop({ default: false }) editable: boolean;
  @Prop({ default: '' }) placeholder: string;
  @Prop({ default: false }) isUpdating: boolean;
  @Prop() mergeValues: IMergeValues;
  @Prop() fields: IField[];

  @Watch('fields')
  @Watch('mergeValues')
  watchMergeValueUpdates() {
    updateMergeValues(this.contentPayload);
  }
  @Watch('content') watchContent() {
    setContent(this.contentPayload);
  }

  @Watch('fieldFocused')
  watchFieldFocused(fieldFocused: boolean) {
    if (fieldFocused) {
      this.viewer.view.focus();
    }
  }

  viewer: TipTapEditor | null = null;

  get contentPayload() {
    return {
      viewer: this.viewer,
      content: this.content,
      fields: this.fields,
      mergeValues: this.mergeValues,
    };
  }

  get inputViewer() {
    return {
      content: this.content,
      editable: this.editable,
      extensions: [
        InputDocNode,
        Focus,
        ParagraphNode,
        Text,
        MergeValueNode,
        History,
        ...(this.bold ? [Bold] : []),
        ...(this.italic ? [Italic] : []),
        ...(this.underline ? [Underline] : []),
        ...(this.superscript ? [Superscript] : []),
        ...(this.multiLine ? [EnterBreakNode] : []),
        ...(this.link
          ? [
              Link.configure({
                autolink: false,
                openOnClick: true,
                linkOnPaste: false,
              }),
            ]
          : []),
      ],
      onBlur: this.update,
      onUpdate: this.debounceUpdate,
      onCreate: () => handleInit(this.viewer, this.fields, this.mergeValues),
      enableInputRules: false,
      enablePasteRules: false,
    };
  }

  get innerTextContent(): string {
    let transaction = this.viewer?.state.tr;

    transaction = ProsemirrorHelper.replaceBrNodes(transaction, this.viewer?.state.schema);

    return ProsemirrorHelper.getStringFromTransaction(transaction, this.viewer?.state.schema);
  }

  update() {
    if (!this.editable) {
      return;
    }

    this.$emit('update', this.innerTextContent);
  }

  debounceUpdate: () => void = debounce(this.update, 300);

  mounted() {
    this.viewer = new TipTapEditor(this.inputViewer);
  }

  beforeDestroy() {
    this.viewer.destroy();
  }
}
